/**
 * drawingFactory.js
 * ----------------------------------------------------------------------------
 * Monta o `drawingProject`: a camada normalizada e única que o componente
 * usa para renderizar. Deriva da ficha técnica + camadas livres (elementos,
 * anotações, imagens).
 *
 * Esta estrutura é o que, no futuro, vira SVG/PDF exportável: tudo que o
 * desenho precisa está aqui, em unidades bem definidas.
 */

import { normalizeFichaTecnica } from "./normalizeFichaTecnica";
import { DEFAULT_METERS_TO_PX } from "./scaleUtils";
import { getContentBounds } from "./measurementUtils";

/**
 * @param {Array} items product.ficha_tecnica
 * @param {object} extras { elements, annotations, images, scale, displayUnit }
 * @returns {object} drawingProject
 */
export function buildDrawingProject(items, extras = {}) {
  const pieces = normalizeFichaTecnica(items);

  return {
    unit: "m",
    displayUnit: extras.displayUnit || "m",
    scale: extras.scale || 1,
    metersToPx: extras.metersToPx || DEFAULT_METERS_TO_PX,

    // peças desenháveis (inclui acabamentos marcados com isFinishing)
    pieces,

    // camadas livres do desenho técnico (não financeiras)
    elements: Array.isArray(extras.elements) ? extras.elements : [],
    annotations: Array.isArray(extras.annotations) ? extras.annotations : [],
    images: Array.isArray(extras.images) ? extras.images : []
  };
}

/**
 * Itens type === "Acabamento": entram na LEGENDA, não no canvas como chapa.
 * Se houver ligação a peça/borda no futuro, vira etiqueta.
 */
export function getFinishingLegend(pieces) {
  return (pieces || [])
    .filter(p => p.isFinishing)
    .map(p => ({
      id: p.id,
      sourceIndex: p.sourceIndex,
      label: p.description || "Acabamento",
      quantity: p.quantity,
      total: p.total
    }));
}

/** Apenas as peças que viram retângulo no canvas (pedra principal/secundária). */
export function getRenderablePieces(pieces) {
  return (pieces || []).filter(p => p.isStone);
}

/**
 * ESCALA NOMINAL DA PRANCHA IMPRESSA.
 *
 * Escolhe a escala "bonita" (1:10, 1:20, 1:25...) mais próxima em que o
 * conteúdo cabe na área de desenho do A4 landscape, e devolve o zoom/pan a
 * aplicar no DrawingCanvas para que o papel saia NAQUELA escala.
 *
 * Premissa de calibração: o SVG (viewBox `canvasW`px) é impresso ocupando a
 * largura útil do A4 landscape (~258mm com margens de 0.6cm + moldura), ou
 * seja K_MM_PER_PX ≈ 258/760. É uma escala declarada NOMINAL — o objetivo é
 * a prancha bater com régua na tolerância de impressão doméstica.
 */
const K_MM_PER_PX = 258 / 760;
const PRETTY_SCALES = [5, 10, 20, 25, 50, 75, 100, 150, 200];

export function getSheetView(pieces, canvasW, canvasH) {
  const bounds = getContentBounds(getRenderablePieces(pieces), {
    scale: 1,
    width: canvasW,
    height: canvasH
  });
  if (!bounds || bounds.w <= 0 || bounds.h <= 0) {
    return { scaleDenominator: 20, zoom: 1, pan: { x: 0, y: 0 } };
  }
  const availWmm = canvasW * K_MM_PER_PX;
  const availHmm = canvasH * K_MM_PER_PX;
  const contentWMeters = bounds.w / DEFAULT_METERS_TO_PX;
  const contentHMeters = bounds.h / DEFAULT_METERS_TO_PX;

  let denominator = PRETTY_SCALES[PRETTY_SCALES.length - 1];
  for (const n of PRETTY_SCALES) {
    if (
      (contentWMeters * 1000) / n <= availWmm &&
      (contentHMeters * 1000) / n <= availHmm
    ) {
      denominator = n;
      break;
    }
  }
  // zoom que faz 1m real medir (1000/N)mm no papel
  const zoom = 1000 / denominator / (DEFAULT_METERS_TO_PX * K_MM_PER_PX);
  return {
    scaleDenominator: denominator,
    zoom,
    pan: {
      x: (canvasW - bounds.w * zoom) / 2 - bounds.x * zoom,
      y: (canvasH - bounds.h * zoom) / 2 - bounds.y * zoom
    }
  };
}
